import React from 'react';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { SlashTriggerContext } from '@/types/slash-commands';

interface OverviewTextareaProps {
  value: string;
  onChange: (value: string) => void;
  isSlashMenuOpen: boolean;
  handleSlashTrigger: (context: SlashTriggerContext) => void;
  handleSlashFilterUpdate: (filterText: string) => void;
  handleSlashClose: () => void;
  handleTextareaKeyDown: (
    event: React.KeyboardEvent<HTMLTextAreaElement>
  ) => void;
  className?: string;
}

export function OverviewTextarea({
  value,
  onChange,
  isSlashMenuOpen,
  handleSlashTrigger,
  handleSlashFilterUpdate,
  handleSlashClose,
  handleTextareaKeyDown,
  className,
}: OverviewTextareaProps) {
  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const textarea = e.target;
    const newValue = textarea.value;
    const cursorPosition = textarea.selectionStart;

    onChange(newValue);

    const typedChar = newValue.charAt(cursorPosition - 1);
    const textBeforeCursor = newValue.substring(0, cursorPosition);
    const slashIndex = textBeforeCursor.lastIndexOf('/');

    // Handle slash command triggers
    if (!isSlashMenuOpen && typedChar === '/') {
      const textBeforeSlash = newValue.substring(0, cursorPosition - 1);

      // Extract word before slash
      const lastSpaceIndex = textBeforeSlash.trimEnd().lastIndexOf(' ');
      const wordBefore =
        lastSpaceIndex === -1
          ? textBeforeSlash.trim()
          : textBeforeSlash.trimEnd().substring(lastSpaceIndex + 1);

      // Extract first sentence for context
      const firstSentenceMatch = newValue.match(/^.*?[.!?](\s+|$)/);
      const firstSentence = firstSentenceMatch
        ? firstSentenceMatch[0].trim()
        : newValue.trim();

      const context: SlashTriggerContext = {
        targetElement: textarea,
        currentParagraphIndex: -1, // Special index for overview
        currentParagraphType: 'conclusion',
        textBeforeTrigger: textBeforeSlash,
        wordBeforeTrigger: wordBefore || null,
        firstSentenceOfParagraph: firstSentence || null,
      };
      handleSlashTrigger(context);
    } else if (isSlashMenuOpen) {
      if (
        slashIndex === -1 ||
        typedChar === ' ' ||
        newValue.length < value.length // Handle deletion
      ) {
        handleSlashClose();
      } else {
        const filter = newValue.substring(slashIndex + 1, cursorPosition);
        handleSlashFilterUpdate(filter);
      }
    }
  };

  return (
    <Card className='relative bg-background/80 shadow-none border border-input p-0 transition-colors duration-150 hover:bg-muted/60 focus-within:bg-muted/60 rounded-md'>
      <CardContent className='p-0'>
        <Textarea
          id='overview'
          placeholder='Write your overview here...'
          value={value}
          onChange={handleTextChange}
          onKeyDown={handleTextareaKeyDown}
          className={cn(
            'min-h-[100px] resize-none border-none bg-transparent focus:ring-0 focus:outline-none text-base px-3 pb-3',
            className
          )}
        />
      </CardContent>
    </Card>
  );
}
